import {
  type ParticipantBehaviour,
  type ParticipantEmbeddableForm,
  type ParticipantQr,
  ParticipantQrStatus,
  type UpdateParticipantQrBehaviourMethodsRequest,
  type UpdateParticipantQrBehaviourRequest,
  type UpdateParticipantQrRequest,
} from '@piccolohealth/pbs-common';
import { ChakraV3 } from '@piccolohealth/ui';
import { z } from '@piccolohealth/util';
import React from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { HookedSubmitButton } from '../../components/forms/V3HookedSubmitButton';
import { createModal } from '../../components/generic/Modal';
import { useUpdateParticipantQRMutation } from '../../graphql/hooks/useUpdateParticipantQRMutation';
import { useAppContext } from '../../hooks/useAppContext';
import { zodResolver } from '../../utils/zod';
import { ParticipantQRForm } from './ParticipantQRForm';

type FormValues = UpdateParticipantQrRequest;

const updateParticipantQrBehaviourMethodsRequestSchema: z.ZodSchema<UpdateParticipantQrBehaviourMethodsRequest> =
  z.object({
    abc: z.object({ enabled: z.boolean() }),
    frequency: z.object({ enabled: z.boolean() }),
    duration: z.object({ enabled: z.boolean() }),
    episodicSeverity: z.object({ enabled: z.boolean() }),
  });

export const behaviourSchema: z.ZodSchema<UpdateParticipantQrBehaviourRequest> = z.object({
  behaviourId: z.string(),
  methods: updateParticipantQrBehaviourMethodsRequestSchema,
});

const schema: z.ZodSchema<FormValues> = z.object({
  name: z.string().nullish(),
  status: z.enum(ParticipantQrStatus),
  behaviours: z.array(behaviourSchema),
  embeddableFormIds: z.array(z.string()),
  labelIds: z.array(z.string()).nullish(),
});

interface Props {
  qr: ParticipantQr;
  participantBehaviours: ParticipantBehaviour[];
  participantId: string;
  participantEmbeddableForms: ParticipantEmbeddableForm[];
}

export const ParticipantQREditModal = createModal<Props>((props) => {
  const { qr, participantBehaviours, participantId, participantEmbeddableForms, modal } = props;

  const { hide, visible, remove } = modal;
  const { organization, successToast, errorToast } = useAppContext();
  const mutation = useUpdateParticipantQRMutation();

  const initialValues: Partial<FormValues> = {
    name: qr.name,
    status: qr.status,
    behaviours: participantBehaviours.map((behaviour) => ({
      behaviourId: behaviour.id,
      methods: qr.behaviours.find((b) => b.id === behaviour.id)?.methods ?? {
        abc: { enabled: false },
        frequency: { enabled: false },
        duration: { enabled: false },
        episodicSeverity: { enabled: false },
      },
    })),
    embeddableFormIds: qr.embeddableForms.map((form) => form.id),
    labelIds: qr.labels.map((label) => label.id),
  };

  const methods = useForm<FormValues>({
    defaultValues: initialValues,
    mode: 'all',
    resolver: zodResolver(schema),
  });

  const onSubmit = React.useCallback(
    async (values: FormValues) => {
      const behavioursToSubmit = (values.behaviours ?? []).filter((behaviour) =>
        Object.values(behaviour.methods).some((method) => method.enabled),
      );

      await mutation
        .mutateAsync({
          organizationId: organization.id,
          participantId,
          qrId: qr.id,
          request: {
            name: values.name,
            status: values.status,
            behaviours: behavioursToSubmit,
            embeddableFormIds: values.embeddableFormIds,
            labelIds: values.labelIds,
          },
        })
        .then(async () => {
          hide();
          successToast('QR code updated successfully');
        })
        .catch((err) => {
          errorToast(`Error updating QR code: ${err.message}`);
        });
    },
    [mutation, organization.id, participantId, qr.id, hide, successToast, errorToast],
  );

  return (
    <ChakraV3.Dialog.Root open={visible} onOpenChange={hide} onExitComplete={remove}>
      <ChakraV3.Dialog.Backdrop />
      <ChakraV3.Dialog.Positioner>
        <FormProvider {...methods}>
          <form onSubmit={methods.handleSubmit(onSubmit)}>
            <ChakraV3.Dialog.Content>
              <ChakraV3.DialogTitleSubtitle
                title='Edit QR code'
                subtitle='Change QR code details'
              />
              <ChakraV3.Dialog.Body>
                <ParticipantQRForm
                  participantBehaviours={participantBehaviours}
                  participantEmbeddableForms={participantEmbeddableForms}
                />
              </ChakraV3.Dialog.Body>
              <ChakraV3.Dialog.Footer>
                <ChakraV3.HStack>
                  <ChakraV3.Button onClick={hide} size='sm' variant='subtle'>
                    Close
                  </ChakraV3.Button>
                  <HookedSubmitButton>Save</HookedSubmitButton>
                </ChakraV3.HStack>
              </ChakraV3.Dialog.Footer>
            </ChakraV3.Dialog.Content>
          </form>
        </FormProvider>
      </ChakraV3.Dialog.Positioner>
    </ChakraV3.Dialog.Root>
  );
});
